/**
 * BillingReturnPage (/billing/return?token=...) — landing do retorno do checkout.
 *
 * Lê o callback_token, chama confirmOrder (que confirma o pagamento no Billing e aplica a
 * fulfillment SÍNCRONA — plano ativo / crédito concedido na hora). Pagamentos assíncronos
 * (PIX/boleto pendentes) caem em "processando" — o webhook conclui depois.
 *
 * ⚠️ CONFIG: Billing:CheckoutReturnBaseUrl deve apontar para o host do Admin
 * (admin.agendario.com.br/billing/return) — P-SVC.
 */
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useQueryClient } from '@tanstack/react-query';
import StandardCard from '../components/ui/StandardCard';
import InlineLoader from '../components/ui/InlineLoader';
import PageLayout from '../components/ui/PageLayout';
import billingService from '../services/billingService';

const SUCCESS = new Set(['Applied', 'AlreadyFulfilled', 'Renewed', 'AlreadyApplied']);

export default function BillingReturnPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = params.get('token');
  const [state, setState] = useState('loading'); // loading | success | pending | error

  useEffect(() => {
    let active = true;
    if (!token) { setState('error'); return; }

    billingService.confirmOrder(token)
      .then((r) => {
        if (!active) return;
        const ok = SUCCESS.has(r.billingOutcome) || SUCCESS.has(r.fulfillmentOutcome);
        setState(ok ? 'success' : 'pending');
        // Atualiza o snapshot (plano/saldo refletem na hora).
        queryClient.invalidateQueries({ queryKey: ['entitlements'] });
      })
      .catch(() => { if (active) setState('error'); });

    return () => { active = false; };
  }, [token, queryClient]);

  const content = {
    loading: { icon: null, title: 'Confirmando seu pagamento…', desc: 'Só um instante.' },
    success: { icon: <CheckCircleIcon color="success" sx={{ fontSize: 56 }} />, title: 'Tudo certo! 🎉', desc: 'Seu plano/pacote já está ativo.' },
    pending: { icon: <HourglassTopIcon color="warning" sx={{ fontSize: 56 }} />, title: 'Pagamento em processamento', desc: 'Assim que o pagamento for confirmado, seu benefício será liberado automaticamente.' },
    error: { icon: <ErrorOutlineIcon color="error" sx={{ fontSize: 56 }} />, title: 'Não consegui confirmar', desc: 'Se o valor foi cobrado, o benefício é liberado automaticamente em instantes. Em caso de dúvida, fale com o suporte.' },
  }[state];

  return (
    <PageLayout
      title="Confirmação de Pagamento"
      icon={<ShoppingCartIcon />}
    >
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 520, mx: 'auto' }}>
        <StandardCard noAccent>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            {state === 'loading' ? <InlineLoader minHeight={120} /> : (
              <Stack spacing={2} alignItems="center">
                {content.icon}
                <Typography variant="h6">{content.title}</Typography>
                <Typography variant="body2" color="text.secondary">{content.desc}</Typography>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={() => navigate('/plans')}>Ver meu plano</Button>
                  <Button onClick={() => navigate('/dashboard')}>Ir para o início</Button>
                </Stack>
              </Stack>
            )}
          </Box>
        </StandardCard>
      </Box>
    </PageLayout>
  );
}
